import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronLeft, ChevronRight, CalendarClock, ChevronDown } from 'lucide-react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
  addYears, subYears, isSameMonth, isToday,
} from 'date-fns';

/**
 * Generic, dependency-free Google-Calendar-style component.
 *
 * Props:
 *  - events: normalized array of { id, date: Date, title, subtitle?, status, stage? }
 *      status ∈ 'overdue' | 'due_soon' | 'pending' | 'completed'
 *  - nounSingular: e.g. 'obligation' or 'task' (used in dialog + empty states)
 *  - onOpenFull?: (event) => void  — if given, shows an action button in the detail dialog
 *  - fullListLabel?: string        — label for that button
 */

const STATUS_STYLES = {
  overdue:   { chip: 'bg-red-100 text-red-800 border-red-300', dot: 'bg-red-500', label: 'Overdue' },
  due_soon:  { chip: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-500', label: 'Due soon' },
  pending:   { chip: 'bg-blue-100 text-blue-800 border-blue-300', dot: 'bg-blue-500', label: 'On track' },
  completed: { chip: 'bg-green-100 text-green-700 border-green-300', dot: 'bg-green-500', label: 'Completed' },
};
const styleFor = (s) => STATUS_STYLES[s] || STATUS_STYLES.pending;

const STATUS_PRIORITY = ['overdue', 'due_soon', 'pending', 'completed'];
const mostUrgent = (evs) => STATUS_PRIORITY.find((s) => evs.some((e) => (e.status || 'pending') === s)) || null;

const DAY_CELL = {
  overdue:   'bg-red-100 text-red-700 hover:bg-red-200',
  due_soon:  'bg-amber-100 text-amber-700 hover:bg-amber-200',
  pending:   'bg-blue-100 text-blue-700 hover:bg-blue-200',
  completed: 'bg-green-100 text-green-700 hover:bg-green-200',
};

const VIEWS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
];

const dayKey = (d) => format(d, 'yyyy-MM-dd');

export default function EventCalendar({ events = [], nounSingular = 'item', onOpenFull, fullListLabel = 'Open' }) {
  const [view, setView] = useState('month');
  const [cursor, setCursor] = useState(new Date());
  const [activeEvent, setActiveEvent] = useState(null);

  // Index by due-day for fast per-cell lookup.
  const byDay = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      if (!e.date) continue;
      const k = dayKey(e.date);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    }
    return map;
  }, [events]);

  const eventsOn = (d) => byDay.get(dayKey(d)) || [];

  const { pending, completed } = useMemo(() => {
    const sorted = (arr) => arr.slice().sort((a, b) => (a.date && b.date ? a.date - b.date : 0));
    return {
      pending: sorted(events.filter((e) => e.status !== 'completed')),
      completed: sorted(events.filter((e) => e.status === 'completed')),
    };
  }, [events]);

  const step = (dir) => {
    const f = { day: addDays, week: addWeeks, month: addMonths, year: addYears };
    const b = { day: subDays, week: subWeeks, month: subMonths, year: subYears };
    setCursor((c) => (dir > 0 ? f[view](c, 1) : b[view](c, 1)));
  };
  const goToday = () => setCursor(new Date());
  const openDay = (d) => { setCursor(d); setView('day'); };

  const periodLabel = () => {
    if (view === 'day') return format(cursor, 'EEEE, d MMMM yyyy');
    if (view === 'week') return `${format(startOfWeek(cursor), 'd MMM')} – ${format(endOfWeek(cursor), 'd MMM yyyy')}`;
    if (view === 'year') return format(cursor, 'yyyy');
    return format(cursor, 'MMMM yyyy');
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-border bg-muted/50">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToday}>Today</Button>
          <div className="flex items-center">
            <Button variant="ghost" size="icon" onClick={() => step(-1)} aria-label="Previous"><ChevronLeft className="w-5 h-5" /></Button>
            <Button variant="ghost" size="icon" onClick={() => step(1)} aria-label="Next"><ChevronRight className="w-5 h-5" /></Button>
          </div>
          <h3 className="text-lg font-semibold text-foreground ml-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{periodLabel()}</h3>
        </div>
        <div className="flex border border-border rounded-lg p-1 bg-card">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === v.key ? 'bg-primary text-white font-medium' : 'text-muted-foreground hover:bg-muted'}`}
              data-testid={`calendar-view-${v.key}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="p-2 sm:p-4">
        {view === 'month' && <MonthView cursor={cursor} eventsOn={eventsOn} onOpenDay={openDay} onOpenEvent={setActiveEvent} />}
        {view === 'week' && <WeekView cursor={cursor} eventsOn={eventsOn} onOpenDay={openDay} onOpenEvent={setActiveEvent} />}
        {view === 'day' && <DayView cursor={cursor} events={eventsOn(cursor)} onOpenEvent={setActiveEvent} noun={nounSingular} />}
        {view === 'year' && <YearView cursor={cursor} eventsOn={eventsOn} onOpenMonth={(m) => { setCursor(m); setView('month'); }} onOpenDay={openDay} />}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 border-t border-border bg-muted/50 text-xs">
        <span className="font-semibold text-muted-foreground">Legend:</span>
        {STATUS_PRIORITY.map((k) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${STATUS_STYLES[k].dot}`} />
            <span className="text-muted-foreground">{STATUS_STYLES[k].label}</span>
          </span>
        ))}
      </div>

      {/* Pending / Completed collapsible lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border-t border-border">
        <StatusList title="Pending" items={pending} emptyText={`No pending ${nounSingular}s 🎉`} onOpenEvent={setActiveEvent} />
        <StatusList title="Completed" items={completed} emptyText="Nothing completed yet" onOpenEvent={setActiveEvent} />
      </div>

      {/* Event detail dialog */}
      <Dialog open={!!activeEvent} onOpenChange={(o) => !o && setActiveEvent(null)}>
        <DialogContent className="sm:max-w-md">
          {activeEvent && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CalendarClock className="w-5 h-5 text-primary" />
                  {activeEvent.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {activeEvent.subtitle && <Row k="Details" v={activeEvent.subtitle} />}
                <Row k="Due date" v={format(activeEvent.date, 'EEEE, d MMMM yyyy')} />
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={styleFor(activeEvent.status).chip}>{styleFor(activeEvent.status).label}</Badge>
                </div>
                {activeEvent.stage && <Row k="Stage" v={activeEvent.stage} capitalize />}
              </div>
              {onOpenFull && (
                <div className="flex justify-end pt-2">
                  <Button className="bg-primary hover:bg-primary/90" onClick={() => { const e = activeEvent; setActiveEvent(null); onOpenFull(e); }}>
                    {fullListLabel}
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v, capitalize }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{k}</span>
      <span className={`font-medium text-foreground ${capitalize ? 'capitalize' : ''}`}>{v}</span>
    </div>
  );
}

function StatusList({ title, items, emptyText, onOpenEvent }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-lg">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-muted/50 hover:bg-muted transition-colors rounded-t-lg"
        aria-expanded={open}
        data-testid={`status-list-toggle-${title.toLowerCase()}`}
      >
        <span className="flex items-center gap-2">
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`} />
          <span className="text-sm font-semibold text-foreground/90">{title}</span>
        </span>
        <span className="text-xs bg-card border border-border rounded-full px-2 py-0.5 text-muted-foreground font-medium">{items.length}</span>
      </button>
      {open && (
        <div className="max-h-64 overflow-y-auto divide-y divide-border border-t border-border">
          {items.length === 0 ? (
            <p className="text-xs text-muted-foreground/70 text-center py-6">{emptyText}</p>
          ) : (
            items.map((ev) => {
              const s = styleFor(ev.status);
              return (
                <button key={ev.id} onClick={() => onOpenEvent(ev)} className="w-full text-left flex items-center gap-2.5 px-3 py-2 hover:bg-muted/50 transition-colors">
                  <span className={`w-2.5 h-2.5 rounded-full ${s.dot} flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {ev.title}
                      {ev.subtitle ? <span className="text-muted-foreground/70 font-normal"> · {ev.subtitle}</span> : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {ev.date && <span className="text-[11px] text-muted-foreground/70">{format(ev.date, 'd MMM yyyy')}</span>}
                    <Badge className={`${s.chip} text-[10px]`}>{s.label}</Badge>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function EventChip({ ev, onClick }) {
  const s = styleFor(ev.status);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(ev); }}
      className={`w-full text-left truncate px-1.5 py-0.5 rounded border text-[11px] leading-tight ${s.chip} hover:brightness-95`}
      title={`${ev.title}${ev.subtitle ? ' — ' + ev.subtitle : ''}`}
    >
      <span className="font-semibold">{ev.title}</span>
      {ev.subtitle ? ` · ${ev.subtitle}` : ''}
    </button>
  );
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function MonthView({ cursor, eventsOn, onOpenDay, onOpenEvent }) {
  const days = eachDayOfInterval({ start: startOfWeek(startOfMonth(cursor)), end: endOfWeek(endOfMonth(cursor)) });
  return (
    <div>
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((d) => <div key={d} className="text-center text-xs font-semibold text-muted-foreground py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-px bg-muted rounded-lg overflow-hidden border border-border">
        {days.map((day) => {
          const evs = eventsOn(day);
          const inMonth = isSameMonth(day, cursor);
          const today = isToday(day);
          return (
            <div key={day.toISOString()} onClick={() => onOpenDay(day)} className={`min-h-[112px] p-1.5 bg-card cursor-pointer hover:bg-muted/50 transition-colors ${inMonth ? '' : 'bg-muted/40'}`}>
              <div className="flex justify-end">
                <span className={`text-xs w-6 h-6 flex items-center justify-center rounded-full ${today ? 'bg-primary text-white font-semibold' : inMonth ? 'text-foreground/90' : 'text-muted-foreground/70'}`}>{format(day, 'd')}</span>
              </div>
              <div className="space-y-0.5 mt-0.5">
                {evs.slice(0, 3).map((ev) => <EventChip key={ev.id} ev={ev} onClick={onOpenEvent} />)}
                {evs.length > 3 && <div className="text-[11px] text-muted-foreground font-medium pl-1">+{evs.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ cursor, eventsOn, onOpenDay, onOpenEvent }) {
  const days = eachDayOfInterval({ start: startOfWeek(cursor), end: endOfWeek(cursor) });
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
      {days.map((day) => {
        const evs = eventsOn(day);
        const today = isToday(day);
        return (
          <div key={day.toISOString()} className={`rounded-lg border min-h-[260px] flex flex-col ${today ? 'border-primary ring-1 ring-primary/30' : 'border-border'}`}>
            <button onClick={() => onOpenDay(day)} className={`text-center py-2 border-b ${today ? 'bg-accent' : 'bg-muted/50'} hover:bg-muted rounded-t-lg`}>
              <div className="text-xs text-muted-foreground">{format(day, 'EEE')}</div>
              <div className={`text-lg font-semibold ${today ? 'text-primary' : 'text-foreground/90'}`}>{format(day, 'd')}</div>
            </button>
            <div className="p-1.5 space-y-1 flex-1">
              {evs.length === 0 ? <p className="text-[11px] text-muted-foreground/50 text-center mt-2">—</p> : evs.map((ev) => <EventChip key={ev.id} ev={ev} onClick={onOpenEvent} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayView({ cursor, events, onOpenEvent, noun }) {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-4">
        <div className="text-sm text-muted-foreground">{format(cursor, 'EEEE')}</div>
        <div className={`text-4xl font-bold ${isToday(cursor) ? 'text-primary' : 'text-foreground'}`}>{format(cursor, 'd')}</div>
        <div className="text-sm text-muted-foreground">{format(cursor, 'MMMM yyyy')}</div>
      </div>
      {events.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground/70 border border-dashed rounded-lg">No {noun}s due on this day</div>
      ) : (
        <div className="space-y-2">
          {events.map((ev) => {
            const s = styleFor(ev.status);
            return (
              <button key={ev.id} onClick={() => onOpenEvent(ev)} className="w-full text-left p-4 rounded-lg border border-border hover:border-primary hover:shadow-sm transition-all flex items-center gap-3">
                <span className={`w-2.5 h-2.5 rounded-full ${s.dot} flex-shrink-0`} />
                <div className="flex-1">
                  <div className="font-semibold text-foreground">{ev.title}</div>
                  {ev.subtitle && <div className="text-sm text-muted-foreground">{ev.subtitle}</div>}
                </div>
                <Badge className={s.chip}>{s.label}</Badge>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function YearView({ cursor, eventsOn, onOpenMonth, onOpenDay }) {
  const year = cursor.getFullYear();
  const months = Array.from({ length: 12 }, (_, i) => new Date(year, i, 1));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {months.map((m) => {
        const days = eachDayOfInterval({ start: startOfWeek(startOfMonth(m)), end: endOfWeek(endOfMonth(m)) });
        return (
          <div key={m.toISOString()} className="border border-border rounded-lg p-3">
            <button onClick={() => onOpenMonth(m)} className="text-sm font-semibold text-foreground hover:text-primary mb-2 w-full text-left">{format(m, 'MMMM')}</button>
            <div className="grid grid-cols-7 gap-0.5">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i} className="text-[9px] text-center text-muted-foreground/70">{d}</div>)}
              {days.map((day) => {
                const evs = eventsOn(day);
                const status = evs.length ? mostUrgent(evs) : null;
                const inMonth = isSameMonth(day, m);
                const today = isToday(day);
                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => status && onOpenDay(day)}
                    disabled={!status}
                    title={status ? `${evs.length} item(s)` : undefined}
                    className={`text-[10px] aspect-square flex items-center justify-center rounded font-semibold ${
                      today ? 'bg-primary text-white font-bold'
                      : status ? `${DAY_CELL[status]} cursor-pointer`
                      : inMonth ? 'text-muted-foreground font-normal' : 'text-muted-foreground/50 font-normal'
                    }`}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
